import { Row } from '@tanstack/react-table'

import { Loader } from '@/ui/Loader'
import { Table, TableColGroup, TableHeader } from '@/ui/Table'
import { TabFilters } from './TabFilters/TabFilters'
import { CreateLogForm } from './components/TableForm/CreateLogForm'
import { useGetLogPermissions } from '../api/useGetLogPermissions'
import { CombinedLogRow } from './components/CombinedLogRow'
import { Reassigned } from './components/Reassigned'
import { Log } from '@/types/logs'
import { Context } from '../context'

import { useCombinedLogsTable } from '../hooks/useCombinedLogsTable'
import { useLeaveSiteAlert } from '../hooks/useLeaveSiteAlert'
import { useCombinedLogsForm } from '../hooks/useCombinedLogsForm'
import { useHandleShiftClick } from '../hooks/useHandleShiftClick'

import styles from './CombinedLogs.module.scss'
import { Fragment, useEffect } from 'react'
import { PreviousShiftLabel } from './components/shiftGrouping/PreviousShiftLabel'
import { HandoverSeparator } from './components/handoverSeparator/HandoverSeparator'
import { useHandleArrowScroll } from '../hooks/useHandleArrowScroll'
import { useAuthStore } from '@/store/auth-store'
import clsx from 'clsx'
import { Role } from '@/types/auth'
import { useClickOutside } from '@/helpers/useClickOutside'

export const CombinedLogs = () => {
    const { table, reassignedTable, hasReassignedLogs, isLoading, isManualSelectionEnabled, setRowSelection } =
        useCombinedLogsTable()
    const { data: logPermissions } = useGetLogPermissions()
    const isAllowedToCreate = logPermissions?.includes('Create')
    const { role } = useAuthStore()

    const { handleShiftClick, logContainerRef, multipleSelection } = useHandleShiftClick()
    const { form, onSubmit, editWrapperRef, isCreateFormActive, setIsCreateFormActive } = useCombinedLogsForm({
        table,
        setRowSelection,
    })
    const { handleKeyDown } = useHandleArrowScroll(logContainerRef)

    const handleRowClick = (row: Row<Log>) => {
        setRowSelection((prev) =>
            isManualSelectionEnabled ? { ...prev, [row.id]: !prev[row.id] } : { [row.id]: true }
        )
    }

    useLeaveSiteAlert({ table, isCreateFormActive })

    const handleRowUnselectClick = () => {
        setRowSelection({})
    }

    useEffect(() => {
        setRowSelection({})
    }, [])

    const wrapperRef = useClickOutside<HTMLTableSectionElement>(handleRowUnselectClick, true)

    if (isLoading) return <Loader />

    return (
        <Context.Provider value={{ form, setIsCreateFormActive }}>
            <section className={styles.root} onKeyDown={handleKeyDown}>
                <div className={styles.logsContainer}>
                    <TabFilters />
                    <form onSubmit={form.handleSubmit(onSubmit)} className={styles.formLayout}>
                        <input type="submit" hidden />
                        {isAllowedToCreate && (
                            <CreateLogForm
                                form={form}
                                isCreateFormActive={isCreateFormActive}
                                setIsCreateFormActive={setIsCreateFormActive}
                                table={table}
                                onSubmit={onSubmit}
                                setRowSelection={setRowSelection}
                            />
                        )}
                        <div
                            data-scrollable-table
                            className={clsx({
                                [styles.tableContainerController]: role === Role.Controller,
                                [styles.tableContainer]: role !== Role.Controller,
                            })}
                            ref={logContainerRef}
                        >
                            <Table className={styles.table}>
                                <TableColGroup table={table} />
                                <TableHeader table={table} />

                                <tbody ref={wrapperRef}>
                                    {hasReassignedLogs &&
                                        reassignedTable.getRowModel().rows.map((row, rowIndex, array) => (
                                            <Reassigned
                                                key={row.id}
                                                currentIndex={rowIndex}
                                                currentRow={row}
                                                array={array}
                                            >
                                                <CombinedLogRow
                                                    row={row}
                                                    rowIndex={rowIndex}
                                                    array={array}
                                                    editWrapperRef={editWrapperRef}
                                                    form={form}
                                                    table={table}
                                                    multipleSelection={multipleSelection}
                                                    setIsCreateFormActive={setIsCreateFormActive}
                                                    onClick={handleRowClick}
                                                    onShiftClick={(event, rowData) =>
                                                        handleShiftClick(event, rowData as Row<Log>)
                                                    }
                                                />
                                            </Reassigned>
                                        ))}
                                    {table.getRowModel().rows.map((row, rowIndex, array) => (
                                        <Fragment key={row.id}>
                                            <PreviousShiftLabel table={table} row={row} />
                                            <HandoverSeparator rowIndex={rowIndex} array={array} />

                                            <CombinedLogRow
                                                key={row.id}
                                                row={row}
                                                rowIndex={rowIndex}
                                                array={array}
                                                editWrapperRef={editWrapperRef}
                                                form={form}
                                                table={table}
                                                multipleSelection={multipleSelection}
                                                setIsCreateFormActive={setIsCreateFormActive}
                                                onShiftClick={(event, rowData) =>
                                                    handleShiftClick(event, rowData as Row<Log>)
                                                }
                                                onClick={handleRowClick}
                                            />

                                            <HandoverSeparator isAfter rowIndex={rowIndex} array={array} />
                                            <PreviousShiftLabel table={table} row={row} isSeparator />
                                        </Fragment>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </form>
                </div>
            </section>
        </Context.Provider>
    )
}
